import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Lock, Phone, MessageCircle, Send, ShieldCheck, Clock, X } from "lucide-react";
import { toast } from "sonner";
import { Link } from "@tanstack/react-router";

import { useAuth } from "@/lib/auth";
import type { ServiceType } from "@/lib/services";
import {
  buildRequestTimeline,
  cancelContactRequest,
  createContactRequest,
  fetchUnlockedContact,
  findExistingRequest,
  normaliseStatus,
  STATUS_LABEL,
  STATUS_CLASS,
  type ContactRequestRow,
  type UnlockedContact,
} from "@/lib/contact-unlock";

interface Props {
  listingId: string;
  ownerId: string | null;
  service: ServiceType;
  showNumber: boolean;
  showWhatsapp: boolean;
  allowUnlock: boolean;
  fallbackPhone?: string | null;
  fallbackWhatsapp?: string | null;
}

export function ContactPanel(props: Props) {
  const { listingId, ownerId, service, showNumber, showWhatsapp, allowUnlock } = props;
  const { user } = useAuth();
  const qc = useQueryClient();
  const publicVisible = showNumber || showWhatsapp;

  const { data: existing, isLoading } = useQuery({
    queryKey: ["contact-unlock", listingId, user?.id],
    queryFn: () => (user ? findExistingRequest(user.id, listingId) : null),
    enabled: !!user && !publicVisible && allowUnlock,
  });

  const status = existing ? normaliseStatus(existing) : null;

  const [unlocked, setUnlocked] = useState<UnlockedContact | null>(null);
  useEffect(() => {
    setUnlocked(null);
    if (existing && normaliseStatus(existing) === "approved") {
      fetchUnlockedContact(existing.id)
        .then(setUnlocked)
        .catch(() => setUnlocked(null));
    }
  }, [existing?.id, existing?.status, existing?.expires_at]);

  const [message, setMessage] = useState("");

  const create = useMutation({
    mutationFn: () =>
      createContactRequest(user!.id, {
        ownerId: ownerId!,
        listingId,
        service,
        message,
      }),
    onSuccess: () => {
      toast.success("Request sent to the owner.");
      setMessage("");
      qc.invalidateQueries({ queryKey: ["contact-unlock", listingId] });
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Could not send request"),
  });

  /* -------- Public contact visible -------- */
  if (publicVisible) {
    return (
      <div className="space-y-2">
        {showNumber && (
          <Row
            icon={<Phone className="h-4 w-4" />}
            label="Phone"
            value={props.fallbackPhone ?? "Shown after enquiry"}
          />
        )}
        {showWhatsapp && (
          <Row
            icon={<MessageCircle className="h-4 w-4" />}
            label="WhatsApp"
            value={props.fallbackWhatsapp ?? "Shown after enquiry"}
          />
        )}
      </div>
    );
  }

  /* -------- Contact hidden -------- */
  if (!allowUnlock) {
    return (
      <div className="flex items-start gap-2 rounded-2xl border border-dashed border-border bg-cream/60 p-3">
        <Lock className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        <p className="text-sm font-medium text-brown-dark">
          The owner has kept contact private. Send an enquiry to reach them.
        </p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="rounded-2xl border border-dashed border-border bg-cream/60 p-3 text-sm">
        <p className="font-medium text-brown-dark">Sign in to request contact.</p>
        <Link
          to="/login"
          className="mt-2 inline-flex rounded-full bg-brand px-3 py-1.5 text-xs font-semibold text-brown-dark shadow-soft"
        >
          Sign in
        </Link>
      </div>
    );
  }

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading contact status…</p>;
  }

  if (!existing || status === "cancelled") {
    return (
      <div className="space-y-2 rounded-2xl border border-dashed border-border bg-cream/60 p-3">
        <div className="flex items-start gap-2">
          <Lock className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          <p className="text-sm font-medium text-brown-dark">
            {status === "cancelled"
              ? "You withdrew your previous request. You can ask again."
              : "The owner keeps contact details private. Ask them to share."}
          </p>
        </div>
        <textarea
          rows={2}
          maxLength={280}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Optional note to the owner (e.g. move-in date, budget)…"
          className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:border-brand"
        />
        <button
          type="button"
          disabled={create.isPending || !ownerId}
          onClick={() => create.mutate()}
          className="inline-flex w-full items-center justify-center gap-1.5 rounded-xl bg-coral py-2.5 text-sm font-semibold text-white shadow-soft disabled:opacity-60"
        >
          <Send className="h-4 w-4" /> Request Contact
        </button>
      </div>
    );
  }

  return (
    <RequestStatusBox
      row={existing}
      unlocked={unlocked}
      onCancelled={() => qc.invalidateQueries({ queryKey: ["contact-unlock", listingId] })}
    />
  );
}

export function RequestTimeline({ row }: { row: ContactRequestRow }) {
  const steps = buildRequestTimeline(row);
  return (
    <ol className="mt-2 space-y-2 border-l border-border pl-3">
      {steps.map((s) => (
        <li key={s.key} className="relative">
          <span
            className={`absolute -left-[17px] top-1 h-2.5 w-2.5 rounded-full ring-2 ring-card ${
              s.state === "upcoming"
                ? "bg-muted-foreground/40"
                : s.tone === "positive"
                  ? "bg-rent"
                  : s.tone === "negative"
                    ? "bg-coral"
                    : "bg-brown-dark"
            }`}
          />
          <div className="flex flex-wrap items-baseline justify-between gap-x-2">
            <span
              className={`text-xs font-semibold ${
                s.state === "upcoming" ? "text-muted-foreground" : "text-brown-dark"
              }`}
            >
              {s.label}
            </span>
            <span className="text-[11px] text-muted-foreground">
              {s.at ? new Date(s.at).toLocaleString() : "—"}
            </span>
          </div>
        </li>
      ))}
    </ol>
  );
}

function RequestStatusBox({
  row,
  unlocked,
  onCancelled,
}: {
  row: ContactRequestRow;
  unlocked: UnlockedContact | null;
  onCancelled?: () => void;
}) {
  const status = normaliseStatus(row);
  const cancel = useMutation({
    mutationFn: () => cancelContactRequest(row.id),
    onSuccess: () => {
      toast.success("Request withdrawn.");
      onCancelled?.();
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : "Could not cancel request"),
  });

  return (
    <div className="space-y-2 rounded-2xl border border-border bg-cream/60 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span
          className={`rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider ${STATUS_CLASS[status]}`}
        >
          {STATUS_LABEL[status]}
        </span>
        <span className="text-[11px] text-muted-foreground">
          {new Date(row.created_at).toLocaleDateString()}
        </span>
      </div>
      {status === "pending" && (
        <p className="text-sm text-brown-dark">Waiting for the owner to approve your request.</p>
      )}
      {status === "rejected" && (
        <p className="text-sm text-brown-dark">
          The owner declined this request.
          {row.response_note && (
            <span className="block text-xs text-muted-foreground">Note: {row.response_note}</span>
          )}
        </p>
      )}
      {status === "expired" && (
        <p className="text-sm text-brown-dark">Approval window expired. Send a fresh request.</p>
      )}
      {status === "approved" && unlocked && (
        <div className="space-y-1.5">
          <div className="flex items-center gap-1 text-[11px] font-semibold text-rent">
            <ShieldCheck className="h-3.5 w-3.5" /> Approved by the owner
          </div>
          {unlocked.phone && (
            <Row
              icon={<Phone className="h-4 w-4" />}
              label="Phone"
              value={unlocked.phone}
              link={`tel:${unlocked.phone}`}
            />
          )}
          {unlocked.whatsapp && (
            <Row
              icon={<MessageCircle className="h-4 w-4" />}
              label="WhatsApp"
              value={unlocked.whatsapp}
              link={`https://wa.me/${unlocked.whatsapp.replace(/[^0-9]/g, "")}`}
            />
          )}
          {!unlocked.phone && !unlocked.whatsapp && (
            <p className="text-xs text-muted-foreground">
              The owner approved your request but has not added contact numbers yet.
            </p>
          )}
          {row.expires_at && (
            <div className="mt-1 inline-flex items-center gap-1 text-[11px] text-muted-foreground">
              <Clock className="h-3 w-3" /> Access ends{" "}
              {new Date(row.expires_at).toLocaleDateString()}
            </div>
          )}
        </div>
      )}

      <RequestTimeline row={row} />

      {status === "pending" && (
        <button
          type="button"
          disabled={cancel.isPending}
          onClick={() => cancel.mutate()}
          className="inline-flex w-full items-center justify-center gap-1.5 rounded-xl border border-coral/40 py-2 text-xs font-semibold text-coral disabled:opacity-60"
        >
          <X className="h-3.5 w-3.5" /> {cancel.isPending ? "Cancelling…" : "Cancel request"}
        </button>
      )}
    </div>
  );
}

function Row({
  icon,
  label,
  value,
  link,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  link?: string;
}) {
  const content = (
    <div className="flex items-center gap-2 rounded-2xl bg-secondary/60 px-3 py-2">
      <span className="text-brown-dark">{icon}</span>
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="ml-auto text-sm font-semibold text-brown-dark">{value}</span>
    </div>
  );
  if (link) {
    return (
      <a href={link} target="_blank" rel="noreferrer" className="block">
        {content}
      </a>
    );
  }
  return content;
}
